import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { swellCart } from '@/lib/swell';

const CartContext = createContext(undefined);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [subTotal, setSubTotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [itemQuantity, setItemQuantity] = useState(0);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const updateCartState = useCallback((data) => {
    if (!data) return;
    setItems(data.items || []);
    setSubTotal(data.sub_total || 0);
    setGrandTotal(data.grand_total || 0);
    setItemQuantity(data.item_quantity || 0);
    setCheckoutUrl(data.checkout_url || null);
  }, []);

  useEffect(() => {
    let handle;
    const fetchCart = () => {
      swellCart.get().then(updateCartState).catch(console.error);
    };

    if ('requestIdleCallback' in window) {
      handle = window.requestIdleCallback(fetchCart);
    } else {
      handle = setTimeout(fetchCart, 1000);
    }

    return () => {
      if ('cancelIdleCallback' in window && typeof handle === 'number') {
        window.cancelIdleCallback(handle);
      } else if (typeof handle === 'number') {
        clearTimeout(handle);
      }
    };
  }, [updateCartState]);

  const addItem = useCallback(async (productId, quantity = 1, options = null, variantId = null) => {
    setLoading(true);
    try {
      const cart = await swellCart.addItem(productId, quantity, options, variantId);
      updateCartState(cart);
      setIsCartOpen(true);
    } catch (err) {
      console.error('Failed to add item:', err);
    } finally {
      setLoading(false);
    }
  }, [updateCartState]);

  const removeItem = useCallback(async (itemId) => {
    setLoading(true);
    try {
      const cart = await swellCart.removeItem(itemId);
      updateCartState(cart);
    } catch (err) {
      console.error('Failed to remove item:', err);
    } finally {
      setLoading(false);
    }
  }, [updateCartState]);

  const updateQuantity = useCallback(async (itemId, quantity) => {
    setLoading(true);
    try {
      const cart = await swellCart.updateItem(itemId, quantity);
      updateCartState(cart);
    } catch (err) {
      console.error('Failed to update quantity:', err);
    } finally {
      setLoading(false);
    }
  }, [updateCartState]);

  const toggleCart = useCallback(() => setIsCartOpen(prev => !prev), []);
  const openCart = useCallback(() => setIsCartOpen(true), []);
  const closeCart = useCallback(() => setIsCartOpen(false), []);

  return (
    <CartContext.Provider value={{
      items, subTotal, grandTotal, itemQuantity, checkoutUrl,
      isCartOpen, loading,
      addItem, removeItem, updateQuantity,
      toggleCart, openCart, closeCart,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within CartProvider');
  return context;
}
